import { DistanceUnitsEnum } from '@ubnt/link-core/enums';
import Text from '@ubnt/ui-components/Text/Text';
import { clsx } from 'clsx';
import convert from 'convert-units';
import { round } from 'lodash-es';
import { useMemo } from 'react';
import { MathUtils, Vector3, type Vector3Tuple } from 'three';
import { calculateAngle } from '@ubnt/wifi-calculator/utils/math';

import { Html } from 'modules/editor/components/Html';

import classes from './LinkDistance.module.scss';

export interface LinkDistanceProps {
  positions: [Vector3Tuple, Vector3Tuple];
  isActive: boolean;
  offset?: number;
  className?: string;
  unit?: ValuesOf<typeof DistanceUnitsEnum>;
}

const getPositionAndAngle = (start: Vector3, end: Vector3) => {
  const angle = calculateAngle([start.x, start.y], [end.x, end.y]);
  const halfLength = start.distanceTo(end) / 2;

  // return early when start/end are equal/overlapping
  if (halfLength === 0) {
    return { angle, position: start.clone() };
  }

  const angleDeg = MathUtils.degToRad(angle);
  const x = Math.cos(angleDeg) * halfLength;
  const y = Math.sin(angleDeg) * halfLength;

  return { angle, position: new Vector3(x + start.x, y + start.y, 0) };
};

export function LinkDistance({
  positions,
  className,
  isActive,
  offset = 0,
  unit = DistanceUnitsEnum.Metric,
}: LinkDistanceProps) {
  const [startPosition, endPosition] = positions;

  const { angle, distance, center } = useMemo(() => {
    const start = new Vector3(...startPosition);
    const end = new Vector3(...endPosition);
    const distance = start.distanceTo(end);
    const { angle, position } = getPositionAndAngle(start, end);

    const getAngle = (angle: number) => {
      // Lets tooltip text be right side up
      if (angle > 90 && angle <= 270) {
        return -angle + 180;
      } else {
        return -angle;
      }
    };

    return {
      angle: getAngle(angle),
      center: position.clone(),
      distance,
    };
  }, [startPosition, endPosition]);

  const displayDistance = useMemo(() => {
    let distanceVal = distance;
    if (unit === DistanceUnitsEnum.Imperial) {
      distanceVal = convert(distance).from('m').to('ft');
    }
    return round(distanceVal, 1);
  }, [distance, unit]);

  if (distance < 1) {
    return null;
  }

  const unitString = unit === DistanceUnitsEnum.Metric ? 'm' : 'ft';

  return (
    <Html position={center} pointerEvents="none" zIndexRange={[]} center>
      <Text
        className={clsx(classes.LinkDistance, isActive && classes.isActive, className)}
        weight="bold"
        style={{
          bottom: offset * Math.cos(MathUtils.degToRad(angle)),
          left: offset * Math.sin(MathUtils.degToRad(angle)),
          transformOrigin: 'bottom left', // needs to match the above style properties
          transform: `rotate(${angle}deg) translate(-50%, 50%)`,
        }}
      >
        {displayDistance} {unitString}
      </Text>
    </Html>
  );
}
